// SECURITY: Production cleanup utilities
// Automatically removes debug information and console logs in production

import { env } from '@/config/environment';

// SECURITY: Conditional logging - only log in development
export const secureLog = {
  log: (...args: unknown[]) => {
    if (env.app.debugMode) {
      console.log(...args);
    }
  },
  
  warn: (...args: unknown[]) => {
    if (env.app.debugMode) {
      console.warn(...args);
    }
  },
  
  error: (...args: unknown[]) => {
    // Always log errors, but sanitize in production
    if (env.app.debugMode) {
      console.error(...args);
    } else {
      // In production, log sanitized version
      console.error('An error occurred. Contact support if this persists.');
    }
  },
  
  debug: (...args: unknown[]) => {
    // Only in development
    if (env.app.debugMode) {
      console.debug(...args);
    }
  },
  
  info: (...args: unknown[]) => {
    if (env.app.debugMode) {
      console.info(...args);
    }
  }
};

// SECURITY: Sanitize sensitive data from logs
export const sanitizeForLog = (data: unknown): unknown => {
  if (!data || typeof data !== 'object') {
    return data;
  }

  const sensitiveKeys = [
    'password', 'token', 'secret', 'key', 'auth', 
    'authorization', 'bearer', 'access_token', 
    'refresh_token', 'session', 'jwt'
  ];

  const sanitized = { ...(data as Record<string, unknown>) };

  for (const key in sanitized) {
    if (sensitiveKeys.some(sensitive => 
      key.toLowerCase().includes(sensitive)
    )) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof sanitized[key] === 'object') {
      sanitized[key] = sanitizeForLog(sanitized[key]);
    }
  }

  return sanitized;
};

// SECURITY: Safe error reporting (no sensitive data)
export const reportError = (error: Error, context?: unknown) => {
  const errorData = {
    message: error.message,
    name: error.name,
    timestamp: new Date().toISOString(),
    context: context ? sanitizeForLog(context) : undefined,
    userAgent: navigator.userAgent,
    url: window.location.href
  };

  if (env.app.debugMode) {
    console.error('🚨 Error Report:', errorData, error.stack);
  } else {
    console.error('An error occurred:', error.message);
    // TODO: Send to a monitoring service if one is configured.
    // Example: monitoringService.captureException(error, { extra: errorData });
  }
};

// SECURITY: Performance monitoring (only in development)
export const performanceLog = (label: string, fn: () => void) => {
  if (env.app.debugMode) {
    const start = performance.now();
    fn();
    const end = performance.now();
  } else {
    fn();
  }
};

// SECURITY: Remove all console.* in production (override global console)
export const setupProductionLogging = () => {
  if (env.app.environment === 'production') {
    // Override console methods in production
    const noop = () => {};
    
    console.log = noop;
    console.debug = noop;
    console.info = noop;
    console.warn = noop;
    // Keep console.error for critical issues
    
    // Remove window.console entirely (prevent access via DevTools)
    if (typeof window !== 'undefined') {
      window.console = {
        ...window.console,
        log: noop,
        debug: noop,
        info: noop,
        warn: noop
      };
    }
  }
};

// SECURITY: Development helper - show environment info
export const showEnvironmentInfo = () => {
  const showEnvInfo = String(import.meta.env.VITE_SHOW_ENV_INFO || '').toLowerCase() === 'true';
  if (env.app.debugMode && showEnvInfo) {
    console.group('🔧 Environment Information');
    console.log('Debug Mode:', env.app.debugMode);
    console.log('Has Supabase Key:', !!env.supabase.anonKey);
    console.groupEnd();
  }
};

// SECURITY: Clear sensitive data from local storage in production
export const clearSensitiveData = () => {
  if (env.app.environment === 'production') {
    // Clear potentially sensitive development data
    const sensitiveKeys = ['debug', 'test', 'dev', 'temp'];
    
    for (const key of Object.keys(localStorage)) {
      if (sensitiveKeys.some(sensitive => key.toLowerCase().includes(sensitive))) {
        localStorage.removeItem(key);
      }
    }
  }
};

// Auto-setup production cleanup on import
if (typeof window !== 'undefined') {
  setupProductionLogging();
  clearSensitiveData();
  showEnvironmentInfo();
}

export default {
  secureLog,
  sanitizeForLog,
  reportError,
  performanceLog,
  setupProductionLogging,
  showEnvironmentInfo,
  clearSensitiveData
}; 
